// What the two reminder surfaces show, worked out from what the server sends.
//
// Pure: no React, no clock, no I/O, so every rule below is unit-testable on its
// own - the same split `day/day-model.ts` and `edit/edit-model.ts` keep.
//
// A REMINDER IS A ROUTINE WITH NO SCHEDULE (AGENTS.md), which is why there is no
// reminder table, no reminder endpoint and no second thing that can be ticked.
// Two surfaces draw them and they are the same rows: a capped block on the day,
// and the full list behind its footer.
//
// NOTHING HERE SORTS THEM. The order - worst-overdue first, then due today, then
// dated, then undated, with a finished one sunk below the open ones - is
// `byReminderOrder` on the SERVER, applied to both the day payload and
// `GET /routines?sectionId=none`. A second copy here would be a second statement
// of one rule, and the two surfaces would drift apart the day either changed.

import type { DayReminder, IsoDay, Routine } from '../api/client.ts';
import { addDays, dayAndMonth, weekdayName } from '../iso-day.ts';
import { wording } from '../shell/voice.ts';
import type { Voice } from '../shell/voice.ts';

/** What the new-reminder sheet sends. Notes stay absent until they contain prose. */
export interface NewReminderInput {
  title: string;
  /** Null is the default and the ordinary case: most reminders need no date. */
  dueDay: IsoDay | null;
  /** Omitted for an unopened, cancelled or whitespace-only optional Notes area. */
  note?: string;
}

/**
 * One reminder, in the shape both surfaces draw.
 *
 * It IS `DayReminder`, deliberately: the day payload is the richer of the two
 * sources, and the full list maps its rows up to this rather than the row
 * component learning two shapes. A row that had to ask which source it came
 * from is a row with two ways to be wrong.
 */
export type ReminderView = DayReminder;

/**
 * A row on the full list, which has no day and therefore no `finishedLater`.
 *
 * `done` there means finished AT ALL rather than finished on some particular
 * day, because the list is not about a day - and `finishedLater` is false for
 * the same reason: it is the answer to "was it open on the day you are looking
 * at", and on this screen there is no such day. The Finished group is what says
 * it is finished here.
 */
export function reminderFromRoutine(routine: Routine): ReminderView {
  return {
    id: routine.id,
    title: routine.title,
    note: routine.note,
    dueDay: routine.dueDay,
    done: routine.completedDay !== null,
    finishedLater: false,
    completedDay: routine.completedDay,
  };
}

// ------------------------------------------------------------- the words ----
//
// EVERY DATE BELOW IS RELATIVE TO THE ACCOUNT'S TODAY, never to the day on
// screen, and that is one decision rather than an oversight. The full list has
// no day at all, so a second anchor would mean the same reminder reading two
// ways on two surfaces - which is exactly what the server computes `meta`
// centrally to prevent for a schedule item. A due day is a fact about the
// reminder, not about the day you happen to be looking at.
//
// These words are the client's, unlike an item's `meta`. The rule they are not
// breaking is about a RECURRENCE - "two clients must not phrase one rule two
// ways" (AGENTS.md) - and a due day is a date, which this client already puts
// into words in its page heading and in every toast (`iso-day.ts`).

/** Inside this many days either side, a weekday name is unambiguous. */
const NEARBY_DAYS = 6;

/**
 * "Due today", "Was due Monday", "Due on 6 September" - or nothing at all.
 *
 * Most reminders have no date, so the empty string is the ordinary answer and
 * the meta line simply has nothing on it.
 */
export function dueWords(dueDay: IsoDay | null, today: IsoDay | null): string {
  // No today, no relative words. A date this client worked out for itself would
  // be the one thing `todayIn()` on the server exists to stop (AGENTS.md), and
  // saying nothing is better than saying something that may be a day out.
  if (dueDay === null || today === null) return '';
  if (dueDay === today) return 'Due today';
  if (dueDay === addDays(today, -1)) return 'Was due yesterday';
  if (dueDay === addDays(today, 1)) return 'Due tomorrow';
  if (dueDay < today) {
    return dueDay >= addDays(today, -NEARBY_DAYS)
      ? `Was due ${weekdayName(dueDay)}`
      : `Was due on ${dayAndMonth(dueDay)}`;
  }
  return dueDay <= addDays(today, NEARBY_DAYS)
    ? `Due ${weekdayName(dueDay)}`
    : `Due on ${dayAndMonth(dueDay)}`;
}

/**
 * Past its date, which is drawn as a WEIGHT CHANGE and never as a colour.
 *
 * Four deliberate absences make an overdue reminder not a nag (prototype-v2.md
 * section 1.4): no danger colour, no effect on any score - reminders are not in
 * the day's figure at all - no badge and no notification, and the create sheet
 * says so out loud. Weight plus words also means it is not colour-only
 * signalling, which a red row would be.
 */
export function isOverdue(dueDay: IsoDay | null, today: IsoDay | null): boolean {
  return dueDay !== null && today !== null && dueDay < today;
}

/** "Finished today", "Finished Monday", "Finished on 6 August". */
export function finishedWords(completedDay: IsoDay | null, today: IsoDay | null): string {
  if (completedDay === null || today === null) return '';
  if (completedDay === today) return 'Finished today';
  if (completedDay === addDays(today, -1)) return 'Finished yesterday';
  return completedDay >= addDays(today, -NEARBY_DAYS)
    ? `Finished ${weekdayName(completedDay)}`
    : `Finished on ${dayAndMonth(completedDay)}`;
}

/**
 * What a row's meta line says, and it says ONE of these three.
 *
 * The line can carry a due date, a finished date and a note glyph, and the rule
 * for it is at most two signals (captain request, 2026-08-13) - so being
 * history REPLACES the due date rather than stacking on top of it. That is also
 * the truer of the two: "Was due Monday" over something you have since done is
 * a complaint about work that is finished.
 */
export type ReminderMeta =
  /** Open on the day being looked at: its due date, if it has one. */
  | { kind: 'due'; words: string; overdue: boolean }
  /** Ticked on the day being looked at, and struck through with it. */
  | { kind: 'done'; words: string }
  /**
   * Open on the day being looked at and finished on a LATER one. History: the
   * reverse-clock glyph, and no tick, because a reminder is finished once and
   * the server would refuse this day's (`already-finished`).
   */
  | { kind: 'history'; words: string };

export function reminderMeta(reminder: ReminderView, today: IsoDay | null): ReminderMeta {
  const when = finishedWords(reminder.completedDay, today);
  // A date-free fallback rather than an empty line, and only where one is
  // needed: the reverse-clock glyph beside no words at all would be a mark
  // nothing explains, to a reader and to a screen reader alike.
  if (reminder.finishedLater)
    return { kind: 'history', words: when === '' ? 'Finished later' : when };
  if (reminder.done) return { kind: 'done', words: when };
  return {
    kind: 'due',
    words: dueWords(reminder.dueDay, today),
    overdue: isOverdue(reminder.dueDay, today),
  };
}

// ------------------------------------------------------- the day's block ----

/**
 * How many rows the block on the day may draw.
 *
 * Two, measured rather than chosen: at three rows the first schedule's header
 * fell below the fold at 390x800 and at two it does not (prototype-v2.md
 * section 1.2). The day screen is for the schedules; the block already costs a
 * head and a foot. The rest are one tap away behind `All reminders`.
 */
export const REMINDERS_ON_A_DAY = 2;

export interface ShownReminders {
  rows: ReminderView[];
  /** Open reminders this block is NOT drawing. The footer's `N more`. */
  hidden: number;
}

/**
 * Which reminders the block draws, and how many it is leaving out.
 *
 * `pinned` is whatever has been ticked in THIS sitting. A ticked row stays on
 * screen struck through rather than vanishing under the finger that ticked it,
 * because the tick is meant to be reversible - and it is appended below the open
 * ones rather than holding its place, so the two rows the block promises are
 * still two things to do.
 */
export function remindersOnDay(
  reminders: readonly ReminderView[],
  pinned: ReadonlySet<string>,
  limit: number = REMINDERS_ON_A_DAY,
): ShownReminders {
  // Not ticked on this day: still open, or finished on some later day and shown
  // here as history. Both are things this day was carrying.
  const live = reminders.filter((r) => !r.done);
  const rows = live.slice(0, limit);
  for (const reminder of reminders) {
    if (pinned.has(reminder.id) && !rows.includes(reminder)) rows.push(reminder);
  }
  // Counted over the SAME population the card head counts, which is `openCount`
  // and not `live`: a history row is not something left to do. Counting it here
  // put `1 more` in the footer of a card whose head read "all done" and whose
  // line read "Nothing left on this list." - one card, two authoritative
  // numbers, and no way for a reader to tell which half is lying. On such a day
  // the door is bare, which is right: nothing to count, and still a way through.
  const drawnOpen = rows.filter((r) => !r.done && !r.finishedLater).length;
  return { rows, hidden: Math.max(0, openCount(reminders) - drawnOpen) };
}

/**
 * How many of this day's reminders are still outstanding.
 *
 * Neither ticked on this day nor finished on a later one. On today that is
 * plainly "still to do"; on a past day it is "of the things that were open
 * then, this many still are", which is the same question asked of that day.
 */
export function openCount(reminders: readonly ReminderView[]): number {
  return reminders.filter((r) => !r.done && !r.finishedLater).length;
}

/**
 * The count in the card head - `5 left`, never `2/5`.
 *
 * THE GRAMMAR IS THE MECHANISM. Every schedule card carries a `done/total`, and
 * `n/n` is the grammar of the things that feed the day's figure; reminders do
 * not feed it (prototype-v2.md section 1.3), so they count in a sentence that
 * cannot be read as one. A user does not have to be told the rule.
 */
export function leftLabel(open: number): string {
  return open === 0 ? 'all done' : `${open} left`;
}

/**
 * The one quiet line under the block, when there is one worth saying.
 *
 * The count above it (`leftLabel`) is the fact and is plain in both voices; this
 * is the sentence beside it, and it is the only thing here the wording moves.
 */
export function remindersLine(open: number, voice: Voice): string {
  if (open === 0) return wording('rem.allDone', voice);
  if (open === 1) return wording('rem.oneLeft', voice);
  return '';
}

// -------------------------------------------------------- the full list ----

export interface ReminderGroups {
  open: ReminderView[];
  finished: ReminderView[];
}

/**
 * To do, and finished for good.
 *
 * Split on `completedDay` rather than on a state column, because there is no
 * state column and deliberately none: a reminder is open exactly when it has no
 * completion row (prototype-v2.md section 1.8). "Done forever" is not enforced
 * anywhere either - it falls out of a reminder having no recurrence to
 * regenerate it.
 */
export function splitReminders(rows: readonly ReminderView[]): ReminderGroups {
  return {
    open: rows.filter((r) => r.completedDay === null),
    finished: rows.filter((r) => r.completedDay !== null),
  };
}
